import React from "react";
import VariantPanel, { type VariantFact } from "@/components/common/VariantPanel";
import { useLanguage } from "@/hooks/useLanguage";
import { pick, type Lang } from "@/lib/i18n";
interface PanelCopy {
  eyebrow: string;
  title: string;
  lead: string;
  points: string[];
  facts: VariantFact[];
  imageAlt: string;
}
const copyData: Record<Lang, PanelCopy> = {
  en: {
    eyebrow: "Mining, Minerals & Aggregates Screening",
    title: "Mining, Minerals & Aggregates Screening",
    lead: "Engineered heavy-duty wire mesh and wedge wire screen panels designed for sizing, classification, and dewatering in mineral processing, crushed rock, and aggregate recovery plants.",
    points: ["Heavy-duty vibrating screen panels engineered for abrasive aggregate classification and particle sizing.", "Accurate sizing and sharp cutoff separation for crushed stone, gravel, ores, and mineral sands.", "Rigid dewatering screen panels and curved sieve bends delivering continuous solid-liquid slurry separation."],
    facts: [{
      label: "Woven Mesh Range",
      value: "1 – 635 Mesh"
    }, {
      label: "Wedge Slot Range",
      value: "0.02 – 5.0 mm"
    }, {
      label: "Alloy Grades",
      value: "AISI 304 / 316L"
    }, {
      label: "Panel Formats",
      value: "Vibrating / Sieve Bend"
    }],
    imageAlt: "Stainless steel heavy-duty vibrating screen panels for mining and aggregates classification"
  },
  zh: {
    eyebrow: "矿山、矿物与骨料筛分",
    title: "矿山、矿物与骨料筛分",
    lead: "专为矿物加工、碎石与骨料回收系统量身定制的重型不锈钢编织筛网与连续缝隙楔形筛板，满足恶劣工况下精确分级与高效脱水需求。",
    points: ["重载高耐磨振动筛板构型，适应碎石、砂石骨料的高冲击连续粒度分级。", "针对破碎矿石、机制砂与矿物精选提供清晰精确的粒度分界与透筛通量。", "刚性高强度脱水筛板与弧形筛设计，实现矿浆连续固液分离与物料回收。"],
    facts: [{
      label: "编织网规格",
      value: "1 – 635 目"
    }, {
      label: "楔形网缝隙",
      value: "0.02 – 5.0 mm"
    }, {
      label: "标准材质",
      value: "AISI 304 / 316L"
    }, {
      label: "板型结构",
      value: "振动筛板 / 弧形筛"
    }],
    imageAlt: "用于矿山与骨料分级的重载不锈钢振动筛板组件"
  },
  es: {
    eyebrow: "Cribado de minería, minerales y áridos",
    title: "Cribado de minería, minerales y áridos",
    lead: "Mallas de alambre de alta resistencia y paneles de alambre en cuña para clasificación, tamizado y deshidratación en procesamiento de minerales y áridos.",
    points: ["Paneles para cribas vibratorias de servicio pesado diseñados para clasificación y corte de áridos abrasivos.", "Calibración precisa y separación nítida para roca triturada, gravas, arenas minerales y minerales metálicos.", "Paneles rígidos de deshidratación y cribas curvas para separación continua de lodos y suspensiones."],
    facts: [{
      label: "Malla tejida",
      value: "1 – 635 Mesh"
    }, {
      label: "Ranura en cuña",
      value: "0,02 – 5,0 mm"
    }, {
      label: "Aleaciones",
      value: "AISI 304 / 316L"
    }, {
      label: "Configuraciones",
      value: "Vibratoria / Curva"
    }],
    imageAlt: "Paneles de criba vibratoria de acero inoxidable para minería y áridos"
  },
  de: {
    eyebrow: "Siebung für Bergbau, Mineralien und Zuschlagstoffe",
    title: "Siebung für Bergbau, Mineralien und Zuschlagstoffe",
    lead: "Robuste Drahtgewebe und Spaltsiebplatten für Trennung, Klassierung und Entwässerung in der Mineralaufbereitung, Schotter- und Zuschlagstoffgewinnung.",
    points: ["Schwere Vibrationssiebbeläge für die Klassierung abrasiver Zuschlagstoffe und zuverlässige Korngrößentrennung.", "Trennscharfe Absiebung von gebrochenem Gestein, Kies, Erzen und mineralischen Sanden.", "Formstabile Entwässerungssiebböden und Bogensiebe für kontinuierliche Fest-Flüssig-Trennung von Schlämmen."],
    facts: [{
      label: "Drahtgewebe",
      value: "1 – 635 Mesh"
    }, {
      label: "Spaltweite",
      value: "0,02 – 5,0 mm"
    }, {
      label: "Werkstoffe",
      value: "AISI 304 / 316L"
    }, {
      label: "Ausführungen",
      value: "Vibrationssieb / Bogensieb"
    }],
    imageAlt: "Edelstahl-Vibrationssiebe für Bergbau und Zuschlagstoffklassierung"
  },
  fr: {
    eyebrow: "Criblage minier, minéraux et granulats",
    title: "Criblage minier, minéraux et granulats",
    lead: "Toiles métalliques lourdes et panneaux à fil profilé pour le calibrage, la classification et l'égouttage dans le traitement des minéraux et granulats.",
    points: ["Panneaux pour cribles vibrants haute résistance conçus pour la classification des granulats abrasifs.", "Coupures nettes et calibrage précis pour roches concassées, graviers, minerais et sables.", "Panneaux d'égouttage rigides et grilles courbes assurant une séparation solide-liquide continue des pulpes."],
    facts: [{
      label: "Gamme tissée",
      value: "1 – 635 mesh"
    }, {
      label: "Fente profilée",
      value: "0,02 – 5,0 mm"
    }, {
      label: "Alliages",
      value: "AISI 304 / 316L"
    }, {
      label: "Formats",
      value: "Vibrant / Grille courbe"
    }],
    imageAlt: "Panneaux de criblage vibrant en acier inoxydable pour minéraux et granulats"
  },
  pt: {
    eyebrow: "Peneiramento de mineração, minerais e agregados",
    title: "Peneiramento de mineração, minerais e agregados",
    lead: "Telas de arame pesadas e painéis de arame em cunha desenvolvidos para classificação, desidratação e peneiramento de minerais, brita e agregados.",
    points: ["Painéis para peneiras vibratórias de alta resistência para classificação de agregados abrasivos.", "Classificação precisa e cortes nítidos para rocha britada, cascalho, minérios e areias minerais.", "Painéis rígidos de desidratação e peneiras curvas para separação sólido-líquido contínua de polpas."],
    facts: [{
      label: "Faixa de malha",
      value: "1 – 635 Mesh"
    }, {
      label: "Ranhura em cunha",
      value: "0,02 – 5,0 mm"
    }, {
      label: "Ligas de aço",
      value: "AISI 304 / 316L"
    }, {
      label: "Formatos",
      value: "Vibratório / Peneira curva"
    }],
    imageAlt: "Painéis de peneiramento vibratório em aço inoxidável para mineração e agregados"
  }
};
export default function MiningPanel() {
  const {
    lang
  } = useLanguage();
  const copy = pick(lang, copyData);
  return <VariantPanel id="mining-screening" eyebrow={copy.eyebrow} title={copy.title} lead={copy.lead} points={copy.points} facts={copy.facts} href="/MiningMineralScreeningPage" image={{
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-industrial-photograph-of-st-85f236.webp",
    alt: copy.imageAlt
  }} tone="muted" />;
}